import * as React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AlertCircle, Loader2, Users } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"

interface JoinGroupModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onJoinGroup: (inviteCode: string) => Promise<void>
  isLoading?: boolean
}

export function JoinGroupModal({
  open,
  onOpenChange,
  onJoinGroup,
  isLoading = false
}: JoinGroupModalProps) {
  const [inviteCode, setInviteCode] = React.useState("")
  const [error, setError] = React.useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!inviteCode.trim()) {
      setError("Please enter an invitation code")
      return
    }

    try {
      await onJoinGroup(inviteCode.trim())
      setInviteCode("")
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to join group")
    }
  }

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setInviteCode("")
      setError(null)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Join a Group
          </DialogTitle>
          <DialogDescription>
            Enter the invitation code shared with you to join an existing group.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="inviteCode">Invitation Code</Label>
              <Input
                id="inviteCode"
                placeholder="Enter invitation code..."
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                disabled={isLoading}
                className="font-mono"
                autoComplete="off"
                autoFocus
              />
              <p className="text-sm text-muted-foreground">
                The code should be provided by a group member
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !inviteCode.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Joining...
                </>
              ) : (
                "Join Group"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}